import {
  PaperClipIcon,
  PencilIcon,
  XMarkIcon,
} from "@heroicons/react/16/solid";
import pluralize from "pluralize";
import { ReactElement, useCallback, useMemo, useState } from "react";

import { getEdgeCountForNodes } from "../../stores/ai-context.ts";
import { NodeGroup } from "../../types/copilot.ts";
import { Badge } from "../catalyst/Badge.tsx";
import { Button } from "../catalyst/Button.tsx";
import { Input } from "../catalyst/Input.tsx";

interface AIContextGroupAttachmentProps {
  group: NodeGroup;
  onRemove: (groupId: string) => void;
  onRename: (groupId: string, newName: string) => void;
  onSelect: (nodeIds: string[]) => void;
}

export const AIContextGroupAttachment = ({
  group,
  onRemove,
  onRename,
  onSelect,
}: AIContextGroupAttachmentProps): ReactElement => {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(group.name);

  // Calculate edge count for this group
  const edgeCount = useMemo(() => {
    return getEdgeCountForNodes(group.nodeIds);
  }, [group.nodeIds]);

  const handleRemove = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      onRemove(group.id);
    },
    [group.id, onRemove],
  );

  const handleClick = useCallback(() => {
    onSelect(group.nodeIds);
  }, [group.nodeIds, onSelect]);

  const handleRename = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    setIsEditing(true);
  }, []);

  const handleSaveName = useCallback(() => {
    if (editName.trim() && editName !== group.name) {
      onRename(group.id, editName.trim());
    }
    setIsEditing(false);
  }, [editName, group.id, group.name, onRename]);

  const handleCancelEdit = useCallback(() => {
    setEditName(group.name);
    setIsEditing(false);
  }, [group.name]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        handleSaveName();
      } else if (e.key === "Escape") {
        handleCancelEdit();
      }
    },
    [handleSaveName, handleCancelEdit],
  );

  return (
    <Badge
      className="flex cursor-pointer items-center gap-2 px-3 py-2 hover:bg-zinc-600/20"
      color="zinc"
      onClick={handleClick}
    >
      <PaperClipIcon className="h-4 w-4 text-zinc-400" />

      {isEditing ? (
        <Input
          autoFocus
          className="flex-1 bg-transparent text-sm"
          onBlur={handleSaveName}
          onChange={(e) => setEditName(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={handleKeyDown}
          value={editName}
        />
      ) : (
        <span className="flex-1 text-sm">
          {group.name} ({group.nodeIds.length}{" "}
          {pluralize("node", group.nodeIds.length)}, {edgeCount}{" "}
          {pluralize("edge", edgeCount)})
        </span>
      )}

      <div className="flex items-center gap-1">
        {!isEditing && (
          <Button
            className="h-6 w-6 p-0"
            color="zinc"
            onClick={handleRename}
            size="sm"
          >
            <PencilIcon className="h-3 w-3" />
          </Button>
        )}
        <Button
          className="h-6 w-6 p-0"
          color="zinc"
          onClick={handleRemove}
          size="sm"
        >
          <XMarkIcon className="h-3 w-3" />
        </Button>
      </div>
    </Badge>
  );
};
